import { useEffect, useMemo, useState } from "react";

import { useParams, useNavigate, useLocation } from "react-router-dom";

import { getContractById } from "../services/contractService";

import type { Contract } from "../types/contract";

import { ReviewSummaryView } from "../components/organisms/ReviewSummaryView";



type LoadState = "loading" | "ready" | "not_found" | "error";

const ReviewPageLoader = () => (
  <div
    className="flex h-screen w-full flex-col items-center justify-center gap-4 bg-background p-6"
    role="status"
    aria-live="polite"
  >
    <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    <p className="text-sm text-muted-foreground">Loading review…</p>
  </div>
);



export const ReviewPage = () => {

  const { id } = useParams<{ id: string }>();

  const navigate = useNavigate();
  const location = useLocation();

  const resolvedFromRoute = useMemo(() => {
    const st = (location.state as { resolvedContract?: Contract } | null) ?? null;
    return st?.resolvedContract ?? null;
  }, [location.state]);

  const [contract, setContract] = useState<Contract | null>(null);

  const [loadState, setLoadState] = useState<LoadState>("loading");

  /** True while re-fetching detail after resolve preloaded the row from POST resolve. */
  const [isSyncingDetail, setIsSyncingDetail] = useState(false);

  useEffect(() => {
    if (!id) {
      return;
    }

    let cancelled = false;

    const hasPreload = resolvedFromRoute?.id === id;
    if (hasPreload) {
      setContract(resolvedFromRoute);
      setLoadState("ready");
      setIsSyncingDetail(true);
    } else {
      setLoadState("loading");
      setContract(null);
      setIsSyncingDetail(false);
    }

    void getContractById(id)
      .then((nextContract) => {
        if (cancelled) {
          return;
        }
        if (nextContract) {
          setContract(nextContract);
          setLoadState("ready");
          return;
        }
        if (!hasPreload) {
          setLoadState("not_found");
        }
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        if (!hasPreload) {
          setLoadState("error");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsSyncingDetail(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [id, resolvedFromRoute]);



  if (!id) {

    return <p className="text-muted-foreground">No contract selected</p>;

  }

  if (loadState === "loading") {

    return <ReviewPageLoader />;

  }

  if (loadState === "not_found") {

    return (

      <p className="text-muted-foreground" role="alert">

        Contract not found.

      </p>

    );

  }

  if (loadState === "error") {

    return (

      <p className="text-destructive" role="alert">

        Unable to load this contract. Try again later.

      </p>

    );

  }

  if (!contract) {

    return <ReviewPageLoader />;

  }



  return (
    <div className="flex flex-col gap-2">
      {isSyncingDetail ? (
        <p
          className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
          role="status"
          aria-live="polite"
        >
          <span
            className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-primary border-t-transparent"
            aria-hidden
          />
          Syncing latest contract details…
        </p>
      ) : null}
      <ReviewSummaryView key={contract.id} contract={contract} onSuccess={() => navigate("/contractiq/contracts")} />
    </div>
  );

};

